import { Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@upcaret/ui';
import { PlayCircle, ArrowRight } from 'lucide-react';
import { DemoSidebar } from './DemoSidebar';
import { DEMO_CONFIGS } from './config';
import { JsonLd } from '../components/seo/json-ld';
import { getSeoEntry } from '../../utils/seo';
import { buildBreadcrumbList } from '../../utils/structured-data';

export default function DemosListPage() {
  const seo = getSeoEntry('/products/agent/demos');

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      buildBreadcrumbList([
        { name: 'Home', item: '/' },
        { name: 'Products', item: '/products' },
        { name: 'AI Customer Support Agent', item: '/products/agent' },
        { name: 'Demos', item: '/products/agent/demos' },
      ]),
      {
        '@type': 'CollectionPage',
        '@id': `${seo.canonical}#collection-page`,
        name: seo.title,
        description: seo.description,
        url: seo.canonical,
        isPartOf: { '@id': 'https://upcaret.com/#website' },
        about: { '@id': 'https://upcaret.com/products/agent#service' },
        publisher: { '@id': 'https://upcaret.com/#organization' },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-linear-to-b from-background via-background to-muted/20">
      <JsonLd data={schema} />
      <div className="container mx-auto px-4 py-6 lg:py-8">
        {/* Two-column layout: Sidebar + Main Content */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full lg:w-64 shrink-0">
            <DemoSidebar />
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                AI agent demos
              </h1>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
                Explore real examples of agents answering questions, taking
                bookings, and supporting customers in different industries. See
                what this could look like for your organization.
              </p>
            </div>

            {/* Demos Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {Object.entries(DEMO_CONFIGS).map(([slug, config]) => (
                <Card
                  key={slug}
                  className="hover:shadow-lg transition-shadow border-2 hover:border-primary/50"
                >
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <PlayCircle className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium text-primary">
                        {config.industry}
                      </span>
                    </div>
                    <CardTitle className="text-xl">{config.title}</CardTitle>
                    <CardDescription className="mt-2">
                      {config.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild className="w-full gap-2">
                      <Link to="/products/agent/demos/$slug" params={{ slug }}>
                        Watch Demo
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA Section */}
            <Card className="bg-primary/5 border-primary/20">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  Ready to create your own agent?
                </CardTitle>
                <CardDescription className="text-base text-center">
                  We&apos;ll build a demo based on your content so you can see
                  how it would support your customers and free up your team.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="gap-2">
                  <Link to="/build-demo">
                    Build Your Demo
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/products/agent/how-it-works">Learn More</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
